import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Loader2, ChevronRight, Sparkles, Store, Globe2, Shield, X, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import RestaurantConfigLocation from './RestaurantConfigLocation';

const INITIAL_LOCATION = {
  address: '',
  city: '',
  country: '',
  region: '',
  comuna: '',
  coordinates: { latitude: 0, longitude: 0 },
};

const INITIAL_FORM = {
  razonSocial: '',
  nombreComercial: '',
  rut: '',
  email: '',
  emailSecundario: '',
  telefono1: '',
  telefono2: '',
  isHoreca: false,
  sucursales: '',
  locationData: { ...INITIAL_LOCATION },
  acceptedTerms: false,
};

const inputClasses =
  'bg-surface-base border-line text-content-primary placeholder:text-gray-600 h-10 text-sm focus:border-kumia-green focus:ring-1 focus:ring-kumia-green/50 rounded-lg transition-colors';

const Field = ({ label, required, children, className = '', error }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-medium text-content-muted uppercase tracking-wider">
      {label}
      {required && <span className="text-kumia-green ml-1">*</span>}
    </Label>
    {children}
    <AnimatePresence>
      {error && (
        <motion.p
          className="text-red-700 dark:text-red-400 text-[11px]"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15 }}
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

/** Parsea negritas **texto** y cursivas _texto_ en una línea y devuelve React nodes */
function parseInlineMd(text) {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  let key = 0;
  let lastIndex = 0;
  const allRegex = /\*\*([^*]+)\*\*|_([^_]+)_/g;
  let match;
  while ((match = allRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }
    if (match[1] !== undefined) {
      parts.push(<strong key={key++} className="text-content-primary font-semibold">{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(<em key={key++} className="text-content-secondary">{match[2]}</em>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }
  return parts.length > 0 ? parts : text;
}

/**
 * Pre-procesa el markdown: une líneas consecutivas de texto plano en un solo
 * párrafo, para que el texto ocupe todo el ancho al justificarse.
 * Respeta: líneas vacías, headings (##/###), bullets (●/○), bloques ```.
 */
function preProcessMd(content) {
  const rawLines = content.split('\n');
  const blocks = [];
  let inCode = false;
  let codeBlock = [];
  let paragraphBuf = [];

  const flushParagraph = () => {
    if (paragraphBuf.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraphBuf.join(' ') });
      paragraphBuf = [];
    }
  };

  const isSpecial = (trimmed, raw) => {
    if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) return true;
    if (/^[●○]\s/.test(trimmed)) return true;
    if ((raw.startsWith('    ') || raw.startsWith('\t')) && /^[●○]\s/.test(trimmed)) return true;
    return false;
  };

  for (const raw of rawLines) {
    const trimmed = raw.trim();
    if (trimmed.startsWith('```')) {
      if (inCode) {
        blocks.push({ type: 'code', lines: [...codeBlock] });
        codeBlock = [];
        inCode = false;
      } else {
        flushParagraph();
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeBlock.push(raw); continue; }
    if (!trimmed) {
      flushParagraph();
      blocks.push({ type: 'blank' });
      continue;
    }
    if (isSpecial(trimmed, raw)) {
      flushParagraph();
      if (trimmed.startsWith('### ')) {
        blocks.push({ type: 'h3', text: trimmed.slice(4) });
      } else if (trimmed.startsWith('## ')) {
        blocks.push({ type: 'h2', text: trimmed.slice(3) });
      } else {
        const isSubBullet = trimmed.startsWith('○');
        const label = trimmed.match(/^[●○]\s/) ? trimmed.slice(2) : trimmed;
        blocks.push({ type: 'bullet', text: label, sub: isSubBullet });
      }
      continue;
    }
    paragraphBuf.push(trimmed);
  }
  flushParagraph();
  if (codeBlock.length > 0) blocks.push({ type: 'code', lines: codeBlock });
  return blocks;
}

/** Convierte el contenido markdown del documento en JSX formateado */
function formatTermsAsJsx(content) {
  if (!content || content.startsWith('No se pudo')) return null;
  const blocks = preProcessMd(content);
  const out = [];
  let isFirstH2 = true;
  let bulletKey = 0;

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    switch (b.type) {
      case 'blank':
        out.push(<div key={`bl-${i}`} className="h-2" />);
        break;
      case 'h2':
        if (isFirstH2) {
          isFirstH2 = false;
          out.push(
            <div key={`h2-${i}`} className="text-center mb-3">
              <h2 className="text-content-primary font-bold text-[17px] tracking-wide uppercase">{parseInlineMd(b.text)}</h2>
            </div>
          );
        } else {
          out.push(
            <div key={`h2-${i}`} className="mt-8 mb-3">
              <h2 className="text-content-primary font-bold text-[15px] tracking-wide uppercase pb-2 border-b border-line">{parseInlineMd(b.text)}</h2>
            </div>
          );
        }
        break;
      case 'h3':
        out.push(
          <div key={`h3-${i}`} className="mt-6 mb-3">
            <h3 className="text-content-primary font-bold text-[14px] tracking-wide uppercase pb-1.5 border-b border-line">{parseInlineMd(b.text)}</h3>
          </div>
        );
        break;
      case 'bullet':
        out.push(
          <div key={`li-${bulletKey++}`} className={`flex gap-2 ${b.sub ? 'pl-5' : 'pl-2'} my-1.5`}>
            <span className={`flex-shrink-0 mt-[5px] w-1.5 h-1.5 rounded-full ${b.sub ? 'bg-surface-5' : 'bg-kumia-green'}`} />
            <span className="text-content-secondary text-[13px] leading-[1.75] text-justify flex-1">{parseInlineMd(b.text)}</span>
          </div>
        );
        break;
      case 'code':
        out.push(
          <pre key={`code-${i}`} className="my-4 p-4 rounded-lg bg-surface-base border border-line text-content-muted text-[13px] leading-relaxed overflow-x-auto">
            {b.lines.join('\n')}
          </pre>
        );
        break;
      case 'paragraph':
      default:
        out.push(
          <p key={`p-${i}`} className="text-content-secondary text-[13px] leading-[1.75] my-2 text-justify">{parseInlineMd(b.text)}</p>
        );
        break;
    }
  }
  return <div>{out}</div>;
}

/** Quita markdown inline para texto plano de PDF */
function stripMd(text) {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/_([^_]+)_/g, '$1');
}

/** Detecta si un párrafo es enteramente negrita (**...**) — subsección */
function isFullBold(text) {
  const t = text.trim();
  return t.startsWith('**') && t.endsWith('**') && t.indexOf('**', 2) === t.length - 2;
}

/** Genera el PDF usando los bloques pre-procesados */
function generateTermsPdf(content) {
  const blocks = preProcessMd(content);
  const doc = new jsPDF('p', 'mm', 'a4');
  const M = 22;
  const PW = 210;
  const PH = 297;
  const CW = PW - 2 * M;
  const FOOTER_ZONE = 20;
  let y = M;
  let page = 1;
  let firstH2 = true;

  const footer = () => {
    doc.setDrawColor(190, 190, 190);
    doc.line(M, PH - 15, PW - M, PH - 15);
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.setFont('helvetica', 'normal');
    doc.text('Aire Digital Solutions SpA \u2014 T\u00e9rminos del Servicio KumIA', M, PH - 10);
    doc.text('P\u00e1gina ' + page, PW - M, PH - 10, { align: 'right' });
  };

  const nextPage = () => { footer(); doc.addPage(); page++; y = M; };

  const need = (h) => { if (y + h > PH - FOOTER_ZONE) nextPage(); };

  const writeLines = (wrappedLines, lineH) => {
    for (const ln of wrappedLines) {
      need(lineH);
      doc.text(ln, doc._currentX ?? M, y);
      y += lineH;
    }
  };

  const textBlock = (text, fontSize, style, indent, lineH, color) => {
    const plain = stripMd(text);
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', style);
    doc.setTextColor(...color);
    doc._currentX = M + indent;
    const lines = doc.splitTextToSize(plain, CW - indent);
    writeLines(lines, lineH);
  };

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    switch (b.type) {
      case 'blank':
        y += 2.5;
        break;

      case 'h2': {
        const title = stripMd(b.text);
        if (firstH2) {
          firstH2 = false;
          need(20);
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 25, 25);
          const tl = doc.splitTextToSize(title, CW);
          for (const l of tl) { doc.text(l, PW / 2, y, { align: 'center' }); y += 6.5; }
          y += 3;
        } else {
          need(16);
          y += 6;
          doc.setFontSize(10.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 25, 25);
          const tl = doc.splitTextToSize(title, CW);
          for (const l of tl) { doc.text(l, M, y); y += 5.5; }
          doc.setDrawColor(100, 100, 100);
          doc.line(M, y, PW - M, y);
          y += 5;
        }
        break;
      }

      case 'h3': {
        const title = stripMd(b.text);
        need(14);
        y += 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(35, 35, 35);
        const tl = doc.splitTextToSize(title, CW);
        for (const l of tl) { doc.text(l, M, y); y += 5; }
        doc.setDrawColor(170, 170, 170);
        doc.line(M, y, PW - M, y);
        y += 4;
        break;
      }

      case 'bullet': {
        const indent = b.sub ? 12 : 6;
        const plain = stripMd(b.text);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(plain, CW - indent);
        need(Math.min(lines.length * 4.2, 10));
        doc.setFillColor(b.sub ? 150 : 70, b.sub ? 150 : 70, b.sub ? 150 : 70);
        doc.circle(M + indent - 3, y - 1, b.sub ? 0.5 : 0.7, 'F');
        doc.setTextColor(50, 50, 50);
        doc._currentX = M + indent;
        writeLines(lines, 4.2);
        y += 1;
        break;
      }

      case 'paragraph':
      default: {
        if (isFullBold(b.text)) {
          need(8);
          textBlock(b.text, 9.5, 'bold', 0, 4.5, [35, 35, 35]);
          y += 1;
        } else {
          textBlock(b.text, 9, 'normal', 0, 4.2, [50, 50, 50]);
          y += 2;
        }
        break;
      }
    }
  }

  footer();
  return doc;
}

const FEATURES = [
  { icon: Store, text: 'Gestión inteligente de tu restaurante' },
  { icon: Sparkles, text: 'Inteligencia artificial aplicada al negocio' },
  { icon: Globe2, text: 'Ecosistema gastronómico conectado' },
  { icon: Shield, text: 'Datos seguros y respaldados' },
];

/* ── Animation variants ── */

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: (i = 0) => ({
    opacity: 1, x: 0,
    transition: { delay: 0.6 + i * 0.1, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const formSectionVariant = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

/* ── Shimmer text effect ── */

const ShimmerText = ({ children, className = '' }) => (
  <span className={`relative inline-block ${className}`}>
    <span className="relative z-10">{children}</span>
    <motion.span
      className="absolute inset-0 z-20 pointer-events-none"
      style={{
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        mixBlendMode: 'overlay',
      }}
      animate={{ backgroundPosition: ['200% center', '-200% center'] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
    >
      {children}
    </motion.span>
  </span>
);

/* ── Floating orb component ── */

const FloatingOrb = ({ size, x, y, delay, duration }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: x,
      top: y,
      background: 'radial-gradient(circle, rgba(135,201,94,0.12) 0%, transparent 70%)',
      filter: 'blur(30px)',
    }}
    animate={{
      y: [0, -20, 0, 15, 0],
      x: [0, 10, -5, 8, 0],
      scale: [1, 1.1, 0.95, 1.05, 1],
      opacity: [0.4, 0.7, 0.5, 0.8, 0.4],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { logout, completeOnboarding, restaurantId } = useAuth();
  const hasNavigated = useRef(false);

  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const termsScrollRef = useRef(null);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleTermsScroll = useCallback(() => {
    const el = termsScrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  const openTermsModal = useCallback(() => {
    if (form.acceptedTerms) {
      updateField('acceptedTerms', false);
      return;
    }
    setHasScrolledToBottom(false);
    setShowTermsModal(true);
  }, [form.acceptedTerms, updateField]);

  const acceptTerms = useCallback(() => {
    updateField('acceptedTerms', true);
    setShowTermsModal(false);
  }, [updateField]);

  useEffect(() => {
    if (!showTermsModal || termsContent !== null) return;
    setTermsLoading(true);
    const base = process.env.PUBLIC_URL || '';
    fetch(`${base}/terminos_y_condiciones.md`)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error('No se pudo cargar el documento'))))
      .then((text) => setTermsContent(text))
      .catch(() => setTermsContent('No se pudo cargar los Términos y Condiciones. Por favor, intente de nuevo.'))
      .finally(() => setTermsLoading(false));
  }, [showTermsModal, termsContent]);

  const downloadTermsPdf = useCallback(() => {
    const content = termsContent || '';
    if (!content || content.startsWith('No se pudo')) return;
    const doc = generateTermsPdf(content);
    doc.save('KumIA-Terminos-y-Condiciones.pdf');
  }, [termsContent]);

  const isLocationFilled = () => {
    const loc = form.locationData || {};
    const hasAddress = loc.address && String(loc.address).trim() !== '';
    const hasCoords = loc.coordinates && (loc.coordinates.latitude !== 0 || loc.coordinates.longitude !== 0);
    return hasAddress || hasCoords;
  };

  const isFormValid = () => {
    const { razonSocial, nombreComercial, rut, email, telefono1, sucursales, acceptedTerms } = form;
    return (
      razonSocial.trim() !== '' && nombreComercial.trim() !== '' && rut.trim() !== '' &&
      email.trim() !== '' && telefono1.trim() !== '' &&
      sucursales !== '' && Number(sucursales) > 0 &&
      isLocationFilled() && acceptedTerms
    );
  };

  const [touched, setTouched] = useState({});

  const markTouched = useCallback((field) => {
    setTouched((prev) => prev[field] ? prev : { ...prev, [field]: true });
  }, []);

  const markAllTouched = useCallback(() => {
    setTouched({
      razonSocial: true, nombreComercial: true, email: true,
      rut: true, telefono1: true, sucursales: true,
      location: true, acceptedTerms: true,
    });
  }, []);

  const getFieldError = (field) => {
    if (field === 'sucursales') {
      if (form.sucursales === '' || form.sucursales === undefined) return 'Este campo es obligatorio';
      if (Number(form.sucursales) <= 0) return 'Debe ser mayor a 0';
      return '';
    }
    if (field === 'location') return !isLocationFilled() ? 'Debes agregar una ubicación' : '';
    if (field === 'acceptedTerms') return !form.acceptedTerms ? 'Debes aceptar los términos y condiciones' : '';
    const value = form[field];
    return !value || String(value).trim() === '' ? 'Este campo es obligatorio' : '';
  };

  const fieldError = (field) => touched[field] ? getFieldError(field) : '';

  const getInputClasses = (field) =>
    fieldError(field)
      ? `${inputClasses} !border-red-500/70 focus:!border-red-500 focus:!ring-red-500/50`
      : inputClasses;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || hasNavigated.current) {
      if (!isFormValid()) markAllTouched();
      return;
    }

    hasNavigated.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await completeOnboarding(form);
      if (result.success) {
        sessionStorage.setItem('onboardingJustCompleted', 'true');
        setTimeout(() => sessionStorage.removeItem('onboardingJustCompleted'), 10000);
        const targetId = result.restaurantId || restaurantId;
        navigate(targetId ? `/${targetId}` : '/', { replace: true });
      } else {
        throw new Error(result.error || 'Error al completar el onboarding');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      hasNavigated.current = false;
      setIsSubmitting(false);
      setSubmitError(error.message || 'Ocurrió un error. Intenta de nuevo.');
    }
  };

  const filledCount = [
    form.razonSocial, form.nombreComercial, form.rut,
    form.email, form.telefono1, form.sucursales,
  ].filter((v) => v && String(v).trim() !== '').length + (isLocationFilled() ? 1 : 0);
  const progressPercent = Math.round((filledCount / 7) * 100);

  return (
    <div className="min-h-screen bg-surface-app flex flex-col lg:flex-row">

      {/* ── Left branding panel (desktop only) ── */}
      <div className="hidden lg:flex lg:w-[420px] xl:w-[480px] flex-shrink-0 flex-col relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a05] via-surface-base to-[#091205]" />

        <FloatingOrb size={180} x="10%" y="15%" delay={0} duration={8} />
        <FloatingOrb size={120} x="60%" y="55%" delay={2} duration={10} />
        <FloatingOrb size={90} x="25%" y="75%" delay={4} duration={7} />
        <FloatingOrb size={140} x="65%" y="10%" delay={1} duration={9} />

        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(135,201,94,1) 1px, transparent 1px), linear-gradient(90deg, rgba(135,201,94,1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <motion.div
          className="absolute top-0 right-0 w-px h-full"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(135,201,94,0.3), transparent)' }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative z-10 flex flex-col h-full px-10 py-10">
          <motion.div
            className="flex items-center gap-2.5 mb-16"
            initial="hidden" animate="visible" variants={fadeUp} custom={0}
          >
            <div className="relative flex-shrink-0 w-14 h-14">
              <motion.div
                className="absolute inset-[-14px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(135,201,94,0.2) 0%, transparent 70%)' }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.img
                src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
                alt="KumIA" className="w-14 h-14 aspect-square object-contain relative z-10"
                style={{ filter: 'drop-shadow(0 0 16px rgba(122,201,67,0.5))' }}
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
            <div className="text-2xl font-bold">
              <span className="text-content-primary">Kum</span>
              <ShimmerText className="text-kumia-green">IA</ShimmerText>
            </div>
          </motion.div>

          <div className="flex-1 flex flex-col justify-start pt-2">
            <motion.h1
              className="text-4xl xl:text-5xl font-bold text-content-primary leading-tight mb-4"
              initial="hidden" animate="visible" variants={fadeUp} custom={1}
            >
              Crea tu<br />
              <motion.span
                className="inline-block"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <ShimmerText className="text-kumia-green">restaurante</ShimmerText>
              </motion.span>
            </motion.h1>

            <motion.p
              className="text-content-muted text-base leading-relaxed mb-10 max-w-sm"
              initial="hidden" animate="visible" variants={fadeUp} custom={2}
            >
              Completa tu perfil para acceder al ecosistema gastronómico más inteligente del mercado.
            </motion.p>

            <div className="space-y-4 mb-10">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={i} className="flex items-center gap-3 group"
                  initial="hidden" animate="visible" variants={slideInLeft} custom={i}
                  whileHover={{ x: 6 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <motion.div
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-kumia-green/10 border border-kumia-green/20 group-hover:bg-kumia-green/20 transition-colors"
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                  >
                    <f.icon size={15} className="text-kumia-green" />
                  </motion.div>
                  <span className="text-sm text-content-secondary group-hover:text-content-primary transition-colors">{f.text}</span>
                </motion.div>
              ))}
            </div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={5}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-content-subtle uppercase tracking-wider">Progreso</span>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={progressPercent} className="text-xs font-semibold text-kumia-green"
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
                  >
                    {progressPercent}%
                  </motion.span>
                </AnimatePresence>
              </div>
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, rgba(135,201,94,0.7), #87C95E, rgba(155,212,108,1))' }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                />
              </div>
            </motion.div>
          </div>

          <motion.p className="text-gray-600 text-xs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.6 }}>
            &copy; {new Date().getFullYear()} <ShimmerText className="text-kumia-green font-medium">KumIA</ShimmerText> &middot; El primer ecosistema gastronómico AI
          </motion.p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Top bar */}
        <header className="sticky top-0 z-50 flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 bg-surface-chrome/80 dark:bg-black/80 backdrop-blur-sm border-b border-line-subtle">
          <div className="flex items-center gap-2 lg:hidden flex-shrink-0">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
              alt="KumIA" className="w-7 h-7 sm:w-8 sm:h-8 aspect-square object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 10px rgba(122,201,67,0.5))' }}
            />
            <span className="text-content-primary font-bold text-sm sm:text-base">Kum</span>
            <span className="text-kumia-green font-bold text-sm sm:text-base">IA</span>
          </div>

          <div className="lg:hidden flex items-center gap-2 sm:gap-3 flex-1 mx-3 sm:mx-4">
            <div className="flex-1 h-1 bg-surface-2 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-kumia-green rounded-full"
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="text-[10px] sm:text-xs text-kumia-green font-semibold">{progressPercent}%</span>
          </div>

          <div className="hidden lg:block">
            <h2 className="text-content-primary font-semibold text-sm">Configuración inicial</h2>
          </div>

          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 sm:gap-2 bg-red-600/10 hover:bg-red-600/20 text-red-700 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-red-600/20 hover:border-red-500/40 flex-shrink-0"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Salir</span>
          </button>
        </header>

        {/* Form */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-14 py-5 sm:py-6 lg:py-8">
          {/* Mobile/Tablet welcome */}
          <motion.div
            className="lg:hidden mb-5 sm:mb-6 relative overflow-hidden rounded-2xl"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative z-10 text-center py-5 sm:py-6 px-4">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-content-primary mb-1">
                Crea tu <ShimmerText className="text-kumia-green">restaurante</ShimmerText>
              </h1>
              <p className="text-content-muted text-xs sm:text-sm">Completa los datos para comenzar</p>
            </div>
            {/* Subtle background glow for mobile header */}
            <div className="absolute inset-0 -z-0" style={{
              background: 'radial-gradient(ellipse at 50% 100%, rgba(135,201,94,0.06) 0%, transparent 70%)',
            }} />
          </motion.div>

          <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-5 sm:space-y-6 lg:space-y-7">

            {/* ── Sección: Empresa ── */}
            <motion.div initial="hidden" animate="visible" variants={formSectionVariant} custom={0}>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-kumia-green/30 to-transparent" />
                <span className="text-[10px] sm:text-xs font-semibold text-kumia-green uppercase tracking-widest">Empresa</span>
                <div className="h-px flex-1 bg-gradient-to-l from-kumia-green/30 to-transparent" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-5 gap-y-3 sm:gap-y-4">
                <Field label="Razón Social" required error={fieldError('razonSocial')}>
                  <Input id="razonSocial" value={form.razonSocial} onChange={(e) => updateField('razonSocial', e.target.value)} onBlur={() => markTouched('razonSocial')} placeholder="Razón social" className={getInputClasses('razonSocial')} />
                </Field>
                <Field label="Nombre Comercial" required error={fieldError('nombreComercial')}>
                  <Input id="nombreComercial" value={form.nombreComercial} onChange={(e) => updateField('nombreComercial', e.target.value)} onBlur={() => markTouched('nombreComercial')} placeholder="Nombre comercial" className={getInputClasses('nombreComercial')} />
                </Field>
                <Field label="Email del restaurante" required error={fieldError('email')}>
                  <Input id="email" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} onBlur={() => markTouched('email')} placeholder="correo@restaurante.com" className={getInputClasses('email')} />
                </Field>
              </div>
            </motion.div>

            {/* ── Sección: Propietario ── */}
            <motion.div initial="hidden" animate="visible" variants={formSectionVariant} custom={1}>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-kumia-green/30 to-transparent" />
                <span className="text-[10px] sm:text-xs font-semibold text-kumia-green uppercase tracking-widest">Propietario</span>
                <div className="h-px flex-1 bg-gradient-to-l from-kumia-green/30 to-transparent" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-5 gap-y-3 sm:gap-y-4">
                <Field label="RUT" required error={fieldError('rut')}>
                  <Input id="rut" value={form.rut} onChange={(e) => updateField('rut', e.target.value)} onBlur={() => markTouched('rut')} placeholder="Ej: 12.345.678-9" className={getInputClasses('rut')} />
                </Field>
                <Field label="Email secundario">
                  <Input id="emailSecundario" type="email" value={form.emailSecundario} onChange={(e) => updateField('emailSecundario', e.target.value)} placeholder="Opcional" className={inputClasses} />
                </Field>
                <Field label="Teléfono principal" required error={fieldError('telefono1')}>
                  <Input id="telefono1" type="tel" value={form.telefono1} onChange={(e) => updateField('telefono1', e.target.value)} onBlur={() => markTouched('telefono1')} placeholder="+56 9 1234 5678" className={getInputClasses('telefono1')} />
                </Field>
                <Field label="Teléfono alternativo">
                  <Input id="telefono2" type="tel" value={form.telefono2} onChange={(e) => updateField('telefono2', e.target.value)} placeholder="Opcional" className={inputClasses} />
                </Field>
              </div>
            </motion.div>

            {/* ── Sección: Negocio ── */}
            <motion.div initial="hidden" animate="visible" variants={formSectionVariant} custom={2}>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-kumia-green/30 to-transparent" />
                <span className="text-[10px] sm:text-xs font-semibold text-kumia-green uppercase tracking-widest">Negocio</span>
                <div className="h-px flex-1 bg-gradient-to-l from-kumia-green/30 to-transparent" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-5 gap-y-3 sm:gap-y-4">
                <Field label="N° de sucursales" required error={fieldError('sucursales')}>
                  <Input id="sucursales" type="number" min="1" value={form.sucursales} onChange={(e) => updateField('sucursales', e.target.value)} onBlur={() => markTouched('sucursales')} placeholder="1" className={getInputClasses('sucursales')} />
                </Field>
                <div className="flex items-end pb-0 sm:pb-1">
                  <div className="flex items-center gap-3 bg-surface-base border border-line rounded-lg px-4 h-10 w-full sm:w-auto">
                    <Checkbox
                      id="isHoreca"
                      checked={form.isHoreca}
                      onCheckedChange={(checked) => updateField('isHoreca', !!checked)}
                      className={`h-4 w-4 border-line-strong ${form.isHoreca ? 'bg-kumia-green border-kumia-green text-black' : ''}`}
                    />
                    <Label htmlFor="isHoreca" className="text-sm text-content-secondary cursor-pointer select-none whitespace-nowrap">
                      Segmento HORECA
                    </Label>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ── Sección: Ubicación (mismo componente que Configuración) ── */}
            <motion.div initial="hidden" animate="visible" variants={formSectionVariant} custom={3}>
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-kumia-green/30 to-transparent" />
                <span className="text-[10px] sm:text-xs font-semibold text-kumia-green uppercase tracking-widest">Ubicación</span>
                <div className="h-px flex-1 bg-gradient-to-l from-kumia-green/30 to-transparent" />
              </div>
              <RestaurantConfigLocation
                data={form.locationData || INITIAL_LOCATION}
                onChange={(value) => updateField('locationData', typeof value === 'function' ? value(form.locationData || INITIAL_LOCATION) : value)}
              />
              <AnimatePresence>
                {fieldError('location') && (
                  <motion.p
                    className="text-red-700 dark:text-red-400 text-[11px] mt-2"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                  >
                    {fieldError('location')}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* ── Terms + Submit ── */}
            <motion.div
              className="border-t border-[#1a1a1a] pt-5 sm:pt-6 pb-4 sm:pb-0"
              initial="hidden" animate="visible" variants={formSectionVariant} custom={4}
            >
              <div className="flex items-start gap-3 mb-5">
                <Checkbox
                  id="acceptedTerms"
                  checked={form.acceptedTerms}
                  onCheckedChange={openTermsModal}
                  className={`mt-0.5 h-4 w-4 border-line-strong flex-shrink-0 ${form.acceptedTerms ? 'bg-kumia-green border-kumia-green text-black' : ''}`}
                />
                <div>
                  <Label className="text-xs text-content-muted select-none leading-relaxed">
                    Acepto los{' '}
                    <span
                      className="text-kumia-green hover:underline cursor-pointer"
                      onClick={(e) => { e.preventDefault(); openTermsModal(); }}
                    >
                      Términos y Condiciones
                    </span>{' '}
                    de KumIA.
                  </Label>
                  <AnimatePresence>
                    {fieldError('acceptedTerms') && (
                      <motion.p
                        className="text-red-700 dark:text-red-400 text-[11px] mt-1"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                      >
                        {fieldError('acceptedTerms')}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full sm:w-auto h-11 px-8 text-black font-semibold text-sm rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group ${
                  isFormValid() && !isSubmitting
                    ? 'bg-kumia-green hover:bg-kumia-green-dark shadow-lg shadow-kumia-green/10 hover:shadow-kumia-green/20'
                    : 'bg-kumia-green opacity-30 cursor-not-allowed shadow-none'
                }`}
                whileHover={isFormValid() && !isSubmitting ? { scale: 1.03 } : {}}
                whileTap={isFormValid() && !isSubmitting ? { scale: 0.97 } : {}}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    Crear mi restaurante
                    <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </motion.button>

              <AnimatePresence>
                {Object.values(touched).some(Boolean) && !isFormValid() && !isSubmitting && (
                  <motion.p
                    className="text-red-700 dark:text-red-400/80 text-[11px] mt-3"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                  >
                    Completa todos los campos obligatorios marcados con <span className="text-kumia-green font-medium">*</span> para continuar.
                  </motion.p>
                )}
              </AnimatePresence>

              {submitError && (
                <motion.div
                  className="mt-4 bg-red-600/10 border border-red-600/20 rounded-lg px-4 py-2.5 text-red-700 dark:text-red-400 text-xs"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitError}
                </motion.div>
              )}
            </motion.div>

          </form>
        </main>
      </div>

      {/* ── Modal Términos y Condiciones ── */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} />

            <motion.div
              className="relative w-full max-w-3xl max-h-[90vh] bg-surface-base border border-line-subtle rounded-2xl shadow-2xl flex flex-col overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="px-6 py-5 border-b border-line-subtle bg-surface-base">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-content-primary tracking-tight">Acuerdo de Licencia SaaS</h2>
                    <p className="text-xs text-content-subtle mt-1">Términos del Servicio KumIA — Aire Digital Solutions SpA</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                    <button
                      type="button"
                      onClick={downloadTermsPdf}
                      disabled={termsLoading || !termsContent || termsContent.startsWith('No se pudo')}
                      className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-content-muted hover:text-content-primary bg-surface-1 hover:bg-surface-3 border border-line rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Download size={14} />
                      PDF
                    </button>
                    <button
                      onClick={() => setShowTermsModal(false)}
                      className="p-1.5 rounded-lg hover:bg-surface-2 text-content-subtle hover:text-content-primary transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              </div>

              <div
                ref={termsScrollRef}
                onScroll={handleTermsScroll}
                className="flex-1 overflow-y-auto px-6 py-6 text-[13px] text-content-secondary leading-[1.7] custom-scrollbar"
              >
                {termsLoading && (
                  <div className="flex items-center justify-center py-12 gap-2 text-content-muted">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Cargando documento...</span>
                  </div>
                )}
                {!termsLoading && termsContent !== null && (
                  formatTermsAsJsx(termsContent)
                )}
                {!termsLoading && termsContent !== null && <div className="h-4" />}
              </div>

              {!hasScrolledToBottom && (
                <div className="px-6 py-2.5 border-t border-line-subtle text-center bg-surface-base">
                  <p className="text-[11px] text-gray-600 animate-pulse">Desplázate hasta el final del documento para poder aceptar</p>
                </div>
              )}

              <div className="px-6 py-4 border-t border-line-subtle bg-surface-base flex items-center justify-between gap-3">
                <p className="text-[11px] text-gray-600 hidden sm:block">Lea el documento completo antes de aceptar.</p>
                <div className="flex items-center gap-3 ml-auto">
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="h-9 px-5 text-sm font-medium text-content-muted hover:text-content-primary bg-surface-1 hover:bg-surface-3 border border-line rounded-xl transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={acceptTerms}
                    disabled={!hasScrolledToBottom || termsLoading || (termsContent && termsContent.startsWith('No se pudo'))}
                    className="h-9 px-6 text-sm font-semibold bg-kumia-green hover:bg-kumia-green-dark text-black rounded-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Acepto los Términos
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingPage;
